import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {cn, DEVICE, useScreenResize} from '@chekin/guestapp-ui-kit';
import {useNavigate} from 'react-router-dom';
import {Gift, Hourglass} from 'lucide-react';
import ChekinProSettings from '../../ChekinProSettings';
import {useSummary} from 'context/summary';
import {useFetchGuruWalkTours, useFetchLockConditions, usePath} from 'hooks';
import {useChekinSDK} from 'context/ChekinSDK';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {useFetchEsimFeature} from 'hooks/esim/useFetchEsimFeature';
import {AUTH_INIT_MODELS, OFFER_STATUSES, LANGUAGE_CODES} from '@chekin/core';
import {PATHS} from 'routes/constants';
import {POSTHOG_EVENTS, usePostHogTrack} from 'analytics';
import BookingDatesBox from 'components/BookingDatesBox';
import CheckInInfoBox from 'components/CheckInInfoBox';
import RemoteAccessNavigationItem from 'components/RemoteAccessNavigationItem';
import HomeViewModals from 'components/HomeViewModals';
import GuidebooksNavigationList from 'components/GuidebooksNavigationList';
import {AiraloBanner} from 'components/AiraloBanner/AiraloBanner';
import {HomeUpsellingList} from 'components/upselling/HomeUpsellingList/HomeUpsellingList';
import {Mozio} from 'components/upselling/Mozio';
import {EndSessionButton} from 'components/sessions/EndSessionButton';
import {ExitReservationButton} from 'components/Kiosko';
import UnavailableFeature from 'components/UnavailableFeature';
import {useReservation} from 'context/reservation';
import {getAccessWindow, isWithinAccessWindow} from 'utils/reservation/accessTimeWindow';
import {
  StyledBox,
  BookingDatesBoxContainer,
  CheckinInfoContainer,
  StyledPage,
  ContentItemElement,
  FreeOffersButton,
} from './styled';

const SUPPORTED_GURUWALK_LANGUAGES = [
  LANGUAGE_CODES.en,
  LANGUAGE_CODES.es,
  LANGUAGE_CODES.de,
  LANGUAGE_CODES.it,
];

const getGuruWalkLanguage = (lang: string) => {
  const appLanguage = lang.slice(0, 2);

  if (SUPPORTED_GURUWALK_LANGUAGES.includes(appLanguage as LANGUAGE_CODES)) {
    return appLanguage;
  }

  return LANGUAGE_CODES.en;
};

const getCityOrCountry = (location?: {city?: string; country?: {name?: string}}) => {
  return location?.city || location?.country?.name;
};

function HomeView() {
  const {t, i18n} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {trackEvent} = usePostHogTrack();
  const {isLoading, data: summary} = useSummary();
  const {isMatch: isMobileMode} = useScreenResize(DEVICE.laptop);
  const {modelName} = useChekinSDK();
  const layoutMode = useLayoutMode();
  const isUniversalLink = modelName === AUTH_INIT_MODELS.housing;
  const showEsimWidget = useFetchEsimFeature();
  const {data: reservation} = useReservation();

  const {data: lockConditions} = useFetchLockConditions({
    enabled: Boolean(summary?.is_remote_access_enabled),
  });

  const showGigSkyBanner =
    summary?.gigsky_status === OFFER_STATUSES.active && !ChekinProSettings.isSDK;

  const showMozio =
    summary?.mozio_status === OFFER_STATUSES.active && !ChekinProSettings.isSDK;

  const showDesktopBanner = !isMobileMode && showGigSkyBanner;
  const isGuruWalkActive =
    summary?.guruwalk_status === OFFER_STATUSES.active && Boolean(summary?.location);
  const guruWalkLocality = isGuruWalkActive
    ? getCityOrCountry(summary?.location)
    : undefined;
  const guruWalkLanguage = getGuruWalkLanguage(i18n.language);
  const {data: guruWalkTours = []} = useFetchGuruWalkTours({
    locality: guruWalkLocality || '',
    country: summary?.location?.country?.name ?? '',
    checkIn: summary?.check_in_date ?? '',
    checkOut: summary?.check_out_date ?? '',
    language: guruWalkLocality ? guruWalkLanguage : '',
    limit: 3,
  });
  const hasGuruWalkDeals = Boolean(guruWalkLocality) && guruWalkTours.length > 0;

  if (isUniversalLink) {
    return null;
  }

  const {startTime, endTime, checkInDate, checkOutDate} = getAccessWindow(
    summary,
    reservation,
  );

  const isInAccessWindow =
    layoutMode !== 'kiosko' ||
    isWithinAccessWindow(checkInDate, startTime, checkOutDate, endTime);

  if (!isInAccessWindow) {
    return (
      <div className="flex h-screen items-center">
        <UnavailableFeature
          title={t('reservation_not_available_yet')}
          subtitle={t('reservation_will_be_available_day_of_chekin', {
            checkInDate: dayjs(checkInDate).format('MMM D'),
            checkOutDate: dayjs(checkOutDate).format('MMM D'),
            startTime: startTime?.slice(0, 5),
            endTime: endTime?.slice(0, 5),
          })}
          reasons={[]}
          icon={<Hourglass size="180" className="" />}
          className="flex flex-col items-center gap-14 max-w-[95%] mx-auto"
        />
      </div>
    );
  }

  return (
    <div>
      {showEsimWidget && layoutMode === 'default' && <AiraloBanner />}
      <StyledPage
        mainHeader={isMobileMode}
        title={t('confirm_booking_details')}
        isLoading={isLoading}
        className="home-view"
      >
        <div
          className={cn(
            'w-full p-0',
            'md:pt-10',
            showDesktopBanner && 'md:pt-[57px]',
            'kiosko:pt-0',
            'kiosko:md:pt-0',
            'home-view__wrapper',
          )}
        >
          <CheckinInfoContainer className="chekin-boxes__container kiosko:flex kiosko:!gap-0">
            <StyledBox
              className={cn(
                'chekin-boxes__tasks-container',
                'kiosko:py-[56px] kiosko:px-[38px] kiosko:rounded-b-[28px] kiosko:rounded-t-[0px]',
              )}
            >
              <CheckInInfoBox />
            </StyledBox>
            <BookingDatesBoxContainer>
              <BookingDatesBox />
            </BookingDatesBoxContainer>
          </CheckinInfoContainer>
        </div>

        {(layoutMode === 'kiosko' ||
          (summary?.is_remote_access_enabled &&
            lockConditions?.is_show_keys_enabled)) && (
          <ContentItemElement>
            <RemoteAccessNavigationItem />
          </ContentItemElement>
        )}

        {layoutMode === 'kiosko' && <ExitReservationButton />}
        {layoutMode === 'default' && (
          <>
            {showMozio && (
              <ContentItemElement>
                <Mozio housingId={summary.id} />
              </ContentItemElement>
            )}

            {summary?.guidebooks?.active && (
              <ContentItemElement>
                <GuidebooksNavigationList />
              </ContentItemElement>
            )}

            {(summary?.upselling?.active || hasGuruWalkDeals) && (
              <ContentItemElement>
                <HomeUpsellingList guruWalkTours={guruWalkTours} />
              </ContentItemElement>
            )}
          </>
        )}

        {hasGuruWalkDeals && (
          <FreeOffersButton
            className="free-offers-navigation-button"
            onClick={() => {
              trackEvent(POSTHOG_EVENTS.FREE_OFFERS_NAVIGATION);
              navigate(getFullPath(PATHS.recommendations.main));
            }}
          >
            <Gift size={18} />
            {t('free_offers')}
          </FreeOffersButton>
        )}

        <HomeViewModals />
        <EndSessionButton />
      </StyledPage>
    </div>
  );
}

export {HomeView};
